import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit3, Trash2, Save, X, Calendar, Eye, EyeOff, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { adminListBlogs, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, adminAuthHeader, API } from '../api';
import ImageUploader from './ImageUploader';
import AdminSearch from './AdminSearch';
import SEOSettingsBlock from './SEOSettingsBlock';

const emptyPost = {
  slug: '',
  title: '',
  excerpt: '',
  author: 'Travelsera Team',
  category: 'India',
  tags: '',
  hero: '',
  fbType: 'default',
  readTime: '5 min read',
  sections: [{ heading: '', body: '' }],
  published: true,
  seo_title: '',
  seo_description: '',
  seo_og_image: '',
  seo_no_index: false,
  seo_no_follow: false,
};

const CATEGORIES = ['India', 'International', 'Family', 'Adventure', 'Honeymoon', 'General'];
const FB_TYPES = ['default', 'beach', 'mountain', 'city', 'family', 'couple', 'temple', 'wildlife', 'adventure'];

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListBlogs();
      setPosts(data || []);
    } catch (_) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setEditing('new');
    setForm(emptyPost);
    setErr('');
  };

  const startEdit = (p) => {
    setEditing(p.slug);
    setForm({
      ...p,
      tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
      sections: p.sections && p.sections.length ? p.sections : [{ heading: '', body: '' }],
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || '',
      seo_og_image: p.seo_og_image || '',
      seo_no_index: !!p.seo_no_index,
      seo_no_follow: !!p.seo_no_follow,
    });
    setErr('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyPost);
    setErr('');
  };

  const updateField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const updateSection = (idx, key, val) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) => (i === idx ? { ...s, [key]: val } : s)),
    }));
  };

  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { heading: '', body: '' }] }));
  const removeSection = (idx) => setForm((f) => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));

  const save = async () => {
    setErr('');
    if (!form.title.trim()) return setErr('Title is required');
    if (!form.excerpt.trim()) return setErr('Excerpt is required');
    const payload = {
      ...form,
      slug: form.slug ? slugify(form.slug) : slugify(form.title),
      tags: typeof form.tags === 'string' ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : (form.tags || []),
      sections: form.sections.filter((s) => (s.heading || '').trim() || (s.body || '').trim()),
    };
    setSaving(true);
    try {
      if (editing === 'new') {
        await adminCreateBlog(payload);
      } else {
        await adminUpdateBlog(editing, payload);
      }
      await load();
      cancel();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    try {
      await adminDeleteBlog(slug);
      await load();
    } catch (_) {}
  };

  if (editing !== null) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {editing === 'new' ? 'New Blog Post' : `Editing: ${form.title || form.slug}`}
          </h3>
          <div className="flex gap-2">
            <button onClick={cancel} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-200 rounded-md">
              <X size={14} className="inline mr-1" /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-1.5 rounded-md disabled:opacity-60">
              <Save size={14} className="inline mr-1" /> {saving ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={(e) => updateField('title', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]" placeholder="Article headline" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL) {editing === 'new' && <span className="text-gray-400">(auto from title)</span>}</label>
            <input value={form.slug} onChange={(e) => updateField('slug', e.target.value)} disabled={editing !== 'new'} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] disabled:bg-gray-50" placeholder="e.g. first-timers-guide-to-bali" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Excerpt *</label>
            <textarea value={form.excerpt} onChange={(e) => updateField('excerpt', e.target.value)} rows={2} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] resize-none" placeholder="One-line summary shown on cards and article top" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Author</label>
            <input value={form.author} onChange={(e) => updateField('author', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={(e) => updateField('category', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] bg-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Read Time</label>
              <input value={form.readTime} onChange={(e) => updateField('readTime', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]" placeholder="e.g. 6 min read" />
            </div>
          </div>
          <div>
            <ImageUploader value={form.hero} onChange={(url) => updateField('hero', url)} label="Hero Image" hint="Upload or paste URL" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fallback Theme</label>
              <select value={form.fbType} onChange={(e) => updateField('fbType', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] bg-white">
                {FB_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.published ? 'published' : 'draft'} onChange={(e) => updateField('published', e.target.value === 'published')} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] bg-white">
                <option value="published">Published</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma separated)</label>
            <input value={form.tags} onChange={(e) => updateField('tags', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]" placeholder="Bali, Beach, First-Timer" />
          </div>
        </div>

        {/* SEO Section — reusable block: title, description, OG image, index & follow toggles */}
        <div className="mt-6">
          <SEOSettingsBlock
            values={{
              seo_title: form.seo_title,
              seo_description: form.seo_description,
              seo_og_image: form.seo_og_image,
              seo_no_index: form.seo_no_index,
              seo_no_follow: form.seo_no_follow,
            }}
            onChange={updateField}
            heading="SEO Settings"
            subheading="Optional overrides for this article. Leave empty to fall back to global defaults."
            testIdPrefix="blog-seo"
          />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700">Sections</h4>
            <button onClick={addSection} className="text-xs text-[#0d64a8] hover:underline">+ Add section</button>
          </div>
          <div className="space-y-3">
            {form.sections.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded-md p-3 bg-[#f9fafb]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-gray-500">Section {i + 1}</span>
                  {form.sections.length > 1 && (
                    <button onClick={() => removeSection(i)} className="text-red-500 hover:text-red-600 text-xs">Remove</button>
                  )}
                </div>
                <input value={s.heading} onChange={(e) => updateSection(i, 'heading', e.target.value)} placeholder="Section heading" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm mb-2 outline-none focus:border-[#0d64a8]" />
                <textarea value={s.body} onChange={(e) => updateSection(i, 'body', e.target.value)} rows={4} placeholder="Paragraph text..." className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] resize-y" />
              </div>
            ))}
          </div>
        </div>

        {err && <div className="mt-4 bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog Posts</h2>
          <p className="text-sm text-gray-500">Write, edit and manage travel guides shown on your public blog.</p>
        </div>
        <button onClick={startNew} className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md">
          <Plus size={15} /> New Post
        </button>
      </div>

      {posts.length > 0 && (
        <div className="mb-4">
          <AdminSearch value={q} onChange={setQ} placeholder="Search title, slug, category, tags..." testId="blog-search" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading blog posts...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <p className="text-gray-700 font-medium">No blog posts yet</p>
          <p className="text-sm text-gray-500 mt-1 mb-5">Start writing your first travel guide.</p>
          <button onClick={startNew} className="bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-2 rounded-md">
            Create your first post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.filter((p) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            const hay = [p.title, p.slug, p.category, (p.tags || []).join(' '), p.excerpt, p.hero_alt]
              .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(s);
          }).map((p) => (
            <div key={p.slug} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-4 hover:shadow-sm transition">
              {p.hero && (
                <img src={p.hero} alt={p.title} className="w-24 h-20 rounded-md object-cover border border-gray-100 shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900 truncate">{p.title}</h3>
                  <span className="text-[10px] bg-[#e8f2fb] text-[#0d64a8] px-2 py-0.5 rounded font-medium">{p.category}</span>
                  {p.published === false ? (
                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium flex items-center gap-1"><EyeOff size={10}/> DRAFT</span>
                  ) : (
                    <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium flex items-center gap-1"><Eye size={10}/> LIVE</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.excerpt}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-2">
                  <span className="flex items-center gap-1"><Calendar size={10}/> {new Date(p.date || p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  <span>by {p.author}</span>
                  <span>/blog/{p.slug}</span>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => startEdit(p)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Edit">
                  <Edit3 size={13} />
                </button>
                <button onClick={() => remove(p.slug)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-500" title="Delete">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManager;
